"use client";

import type { ClaimButtonProps } from "../types";
import { GiftBoxBottomIcon, GiftBoxTopIcon, GiftPandaIcon } from "@/components/icons";
import { OvalLoader } from "@/components/shared/Loaders/Loader";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ClaimAnimatedIcon = () => (
  <div className="claim-animated-icon relative mb-1 flex flex-col items-center leading-none">
    <div className="relative z-20">
      <GiftBoxTopIcon />
    </div>
    <div className="absolute inset-x-0 top-[4px] flex h-[10px] items-center justify-center overflow-hidden">
      <div className="relative scale-[0.9]">
        <GiftPandaIcon />
      </div>
    </div>
    <div className="relative z-10 -translate-y-[3px] transform">
      <GiftBoxBottomIcon />
    </div>
  </div>
);

export const ClaimButtonWeb = ({
  text,
  reward,
  variant = "animated",
  buttonVariant = "primary",
  buttonSize = "medium",
  disabledText,
  onClick,
  fullWidth = false,
}: ClaimButtonProps) => {
  const disabled = variant === "disabled";
  const loading = variant === "loading";
  const animated = variant === "animated";
  const maintenance = variant === "maintenance";
  const hasReward = Boolean(reward && reward.trim());
  const longestLabel = hasReward && (reward?.length ?? 0) >= text.length ? (reward ?? "") : text;

  const hoverable = animated && !loading && !disabled && !maintenance;
  const centerContent = fullWidth && !loading && !disabled && !maintenance;

  return (
    <Button
      onClick={() => !loading && !maintenance && onClick?.()}
      disabled={disabled}
      variant={buttonVariant}
      size={buttonSize}
      className={cn(
        "claim-button-web",
        animated && "claim-button-web--animated",
        "relative overflow-hidden inline-flex min-w-0",
        "text-sm! font-bold! normal-case!",
        hoverable && "group",
        fullWidth ? "w-full" : "w-auto",
        maintenance && "cursor-not-allowed bg-cede-text-inactive hover:bg-cede-text-inactive",
        loading || disabled || maintenance || centerContent ? "justify-center!" : "justify-start!",
      )}
    >
      {!loading && !disabled && !maintenance && (
        <>
          {animated && <ClaimAnimatedIcon />}
          <div className="relative h-[25px] flex-none overflow-hidden text-current text-center">
            <span className="invisible block whitespace-nowrap text-center" aria-hidden="true">
              {longestLabel}
            </span>
            <span
              className={cn(
                "claim-text pointer-events-none absolute inset-0 flex items-center justify-center text-center transition-transform duration-500 ease-in-out",
                hoverable && "group-hover:-translate-y-full",
              )}
            >
              {text}
            </span>
            {hasReward && (
              <span
                className={cn(
                  "reward-text pointer-events-none absolute inset-0 flex items-center justify-center text-center transition-transform duration-500 ease-in-out translate-y-[50px]",
                  hoverable && "group-hover:translate-y-0",
                )}
              >
                {reward}
              </span>
            )}
          </div>
        </>
      )}
      {loading && !disabled && !maintenance && <OvalLoader width={25} height={25} color="var(--color-cede-bg-paper)" />}
      {disabled && disabledText}
      {maintenance && text}
    </Button>
  );
};
